import React from "react";

/**
 * ServiceMarker — bespoke SVG markers, each mapped to a specific service
 * tile so the visual language reinforces the meaning of that tile.
 *
 * Three visual "worlds":
 *  • Commercial Proof  → signal, mapping, intelligence, target logic
 *  • Commercial Arm    → representation, movement, cadence, follow-through
 *  • Commercial Build  → structure, layers, architecture, operating grid
 *
 * All markers share:
 *  • 48x48 canvas
 *  • #C9A961 stroke, 1 stroke-width, round joins
 *  • No fills (except tiny accent dots)
 *  • Line-based, minimal, luxury feel
 */

const STROKE = "#C9A961";
const SW = 1;

function Frame({ children }) {
  return (
    <svg
      viewBox="0 0 48 48"
      width="42"
      height="42"
      fill="none"
      stroke={STROKE}
      strokeWidth={SW}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

// ---- COMMERCIAL PROOF WORLD -----------------------------------------

const SignalRadar = () => (
  <Frame>
    <circle cx="10" cy="38" r="1.5" fill={STROKE} />
    <path d="M 10 38 A 12 12 0 0 1 22 26" />
    <path d="M 10 38 A 20 20 0 0 1 30 18" />
    <path d="M 10 38 A 28 28 0 0 1 38 10" />
    <line x1="10" y1="38" x2="38" y2="10" opacity="0.4" />
  </Frame>
);

const MapNodes = () => (
  <Frame>
    <circle cx="10" cy="14" r="2" />
    <circle cx="28" cy="10" r="2" />
    <circle cx="38" cy="24" r="2" />
    <circle cx="24" cy="34" r="2" />
    <circle cx="12" cy="30" r="2" />
    <line x1="12" y1="14" x2="26" y2="10" opacity="0.55" />
    <line x1="28" y1="12" x2="36" y2="22" opacity="0.55" />
    <line x1="36" y1="24" x2="26" y2="33" opacity="0.55" />
    <line x1="22" y1="34" x2="14" y2="31" opacity="0.55" />
    <line x1="12" y1="28" x2="12" y2="16" opacity="0.55" />
    <line x1="14" y1="14" x2="24" y2="32" opacity="0.35" strokeDasharray="1.5 2.5" />
  </Frame>
);

const Crosshair = () => (
  <Frame>
    <circle cx="24" cy="24" r="14" />
    <circle cx="24" cy="24" r="7" />
    <circle cx="24" cy="24" r="1.6" fill={STROKE} />
    <line x1="6" y1="24" x2="16" y2="24" />
    <line x1="32" y1="24" x2="42" y2="24" />
    <line x1="24" y1="6" x2="24" y2="16" />
    <line x1="24" y1="32" x2="24" y2="42" />
  </Frame>
);

const NarrativeArc = () => (
  <Frame>
    <line x1="6" y1="34" x2="42" y2="34" opacity="0.5" />
    <circle cx="10" cy="34" r="1.4" fill={STROKE} />
    <circle cx="38" cy="34" r="1.4" fill={STROKE} />
    <path d="M 10 34 Q 24 6 38 34" />
    <circle cx="24" cy="15" r="1.6" fill={STROKE} />
    <line x1="24" y1="8" x2="24" y2="15" opacity="0.6" />
  </Frame>
);

const TargetList = () => (
  <Frame>
    <line x1="8" y1="12" x2="8" y2="12.1" strokeWidth="2.5" />
    <line x1="14" y1="12" x2="40" y2="12" />
    <line x1="8" y1="20" x2="8" y2="20.1" strokeWidth="2.5" />
    <line x1="14" y1="20" x2="40" y2="20" />
    <line x1="8" y1="28" x2="8" y2="28.1" strokeWidth="2.5" />
    <line x1="14" y1="28" x2="34" y2="28" />
    <line x1="8" y1="36" x2="8" y2="36.1" strokeWidth="2.5" />
    <line x1="14" y1="36" x2="30" y2="36" />
    <circle cx="8" cy="12" r="0.9" fill={STROKE} />
    <circle cx="8" cy="20" r="0.9" fill={STROKE} />
    <circle cx="8" cy="28" r="0.9" fill={STROKE} />
    <circle cx="8" cy="36" r="0.9" fill={STROKE} />
  </Frame>
);

const DialogDots = () => (
  <Frame>
    <path d="M 8 18 Q 8 12 14 12 L 24 12 Q 30 12 30 18 L 30 22 Q 30 28 24 28 L 18 28 L 14 32 L 14 28 Q 8 28 8 22 Z" />
    <path d="M 18 30 Q 18 26 24 26 L 34 26 Q 40 26 40 30 L 40 34 Q 40 40 34 40 L 24 40 L 20 44 L 20 40 Q 18 40 18 36 Z" transform="translate(0 -2)" opacity="0.75" />
    <circle cx="14" cy="20" r="0.9" fill={STROKE} />
    <circle cx="19" cy="20" r="0.9" fill={STROKE} />
    <circle cx="24" cy="20" r="0.9" fill={STROKE} />
  </Frame>
);

const ForkArrow = () => (
  <Frame>
    <line x1="8" y1="24" x2="22" y2="24" />
    <path d="M 22 24 L 34 12" />
    <path d="M 22 24 L 34 36" />
    <polyline points="30,12 34,12 34,16" />
    <polyline points="30,36 34,36 34,32" />
    <circle cx="8" cy="24" r="1.4" fill={STROKE} />
  </Frame>
);

// ---- COMMERCIAL ARM WORLD -------------------------------------------

const PinLocation = () => (
  <Frame>
    <path d="M 24 8 C 15 8 12 15 12 20 C 12 27 24 40 24 40 C 24 40 36 27 36 20 C 36 15 33 8 24 8 Z" />
    <circle cx="24" cy="20" r="4" />
    <line x1="14" y1="43" x2="34" y2="43" opacity="0.4" />
  </Frame>
);

const RhythmBars = () => (
  <Frame>
    <line x1="9"  y1="30" x2="9"  y2="40" />
    <line x1="16" y1="24" x2="16" y2="40" />
    <line x1="23" y1="16" x2="23" y2="40" />
    <line x1="30" y1="20" x2="30" y2="40" />
    <line x1="37" y1="12" x2="37" y2="40" />
    <line x1="6"  y1="40" x2="41" y2="40" opacity="0.55" />
    <line x1="6"  y1="8"  x2="41" y2="8"  opacity="0.15" strokeDasharray="1.5 3" />
  </Frame>
);

const FollowThrough = () => (
  <Frame>
    <line x1="6" y1="24" x2="30" y2="24" />
    <polyline points="16,18 22,24 16,30" opacity="0.55" />
    <polyline points="22,18 28,24 22,30" opacity="0.75" />
    <polyline points="28,18 34,24 28,30" />
    <circle cx="6" cy="24" r="1.4" fill={STROKE} />
    <circle cx="42" cy="24" r="1.4" />
  </Frame>
);

const Funnel = () => (
  <Frame>
    <path d="M 8 10 L 40 10 L 30 24 L 30 34 L 18 40 L 18 24 Z" />
    <line x1="8" y1="10" x2="40" y2="10" />
    <line x1="14" y1="17" x2="34" y2="17" opacity="0.4" />
    <line x1="20" y1="24" x2="28" y2="24" opacity="0.4" />
  </Frame>
);

const Converging = () => (
  <Frame>
    <line x1="6" y1="10" x2="26" y2="24" />
    <line x1="6" y1="24" x2="26" y2="24" />
    <line x1="6" y1="38" x2="26" y2="24" />
    <circle cx="26" cy="24" r="2" fill={STROKE} />
    <line x1="28" y1="24" x2="42" y2="24" opacity="0.6" />
    <polyline points="38,20 42,24 38,28" opacity="0.6" />
  </Frame>
);

const LoopFlow = () => (
  <Frame>
    <path d="M 34 24 A 10 10 0 1 1 14 24 A 10 10 0 0 1 34 20" />
    <polyline points="30,17 34,20 34,16" />
    <circle cx="24" cy="24" r="1.4" fill={STROKE} />
  </Frame>
);

const Ripple = () => (
  <Frame>
    <circle cx="24" cy="24" r="4" />
    <circle cx="24" cy="24" r="9" opacity="0.7" />
    <circle cx="24" cy="24" r="14" opacity="0.45" />
    <circle cx="24" cy="24" r="19" opacity="0.2" />
    <circle cx="24" cy="24" r="1.4" fill={STROKE} />
  </Frame>
);

const Branches = () => (
  <Frame>
    <line x1="8" y1="24" x2="18" y2="24" />
    <line x1="18" y1="24" x2="26" y2="12" />
    <line x1="18" y1="24" x2="26" y2="36" />
    <line x1="26" y1="12" x2="38" y2="10" />
    <line x1="26" y1="12" x2="38" y2="18" />
    <line x1="26" y1="36" x2="38" y2="34" />
    <line x1="26" y1="36" x2="38" y2="40" />
    <circle cx="18" cy="24" r="1.6" fill={STROKE} />
    <circle cx="26" cy="12" r="1.4" fill={STROKE} />
    <circle cx="26" cy="36" r="1.4" fill={STROKE} />
  </Frame>
);

const DotCluster = () => (
  <Frame>
    <circle cx="14" cy="14" r="2.2" />
    <circle cx="24" cy="10" r="2.2" />
    <circle cx="34" cy="16" r="2.2" />
    <circle cx="18" cy="24" r="2.2" fill={STROKE} />
    <circle cx="30" cy="28" r="2.2" />
    <circle cx="14" cy="36" r="2.2" />
    <circle cx="26" cy="38" r="2.2" />
    <circle cx="38" cy="34" r="2.2" />
    <line x1="18" y1="24" x2="24" y2="10" opacity="0.4" />
    <line x1="18" y1="24" x2="14" y2="14" opacity="0.4" />
    <line x1="18" y1="24" x2="30" y2="28" opacity="0.4" />
  </Frame>
);

const IntegratedGrid = () => (
  <Frame>
    <rect x="8"  y="8"  width="10" height="10" />
    <rect x="20" y="8"  width="10" height="10" />
    <rect x="32" y="8"  width="8"  height="10" />
    <rect x="8"  y="20" width="10" height="10" />
    <rect x="20" y="20" width="10" height="10" strokeWidth="1.4" />
    <line x1="22" y1="22" x2="28" y2="28" strokeWidth="1.4" />
    <line x1="28" y1="22" x2="22" y2="28" strokeWidth="1.4" />
    <rect x="32" y="20" width="8"  height="10" />
    <rect x="8"  y="32" width="10" height="8" />
    <rect x="20" y="32" width="10" height="8" />
    <rect x="32" y="32" width="8"  height="8" />
  </Frame>
);

// ---- COMMERCIAL BUILD WORLD -----------------------------------------

const FoundationLayers = () => (
  <Frame>
    <polygon points="8,14 24,8 40,14 24,20" />
    <line x1="8" y1="14" x2="8" y2="20" />
    <line x1="40" y1="14" x2="40" y2="20" />
    <line x1="24" y1="20" x2="24" y2="26" opacity="0.35" strokeDasharray="1.5 2.5" />
    <line x1="10" y1="26" x2="38" y2="26" />
    <line x1="10" y1="26" x2="10" y2="32" />
    <line x1="38" y1="26" x2="38" y2="32" />
    <line x1="10" y1="32" x2="38" y2="32" />
    <line x1="12" y1="36" x2="36" y2="36" />
    <line x1="12" y1="40" x2="36" y2="40" opacity="0.5" />
  </Frame>
);

const HierarchyTree = () => (
  <Frame>
    <rect x="18" y="6" width="12" height="8" />
    <line x1="24" y1="14" x2="24" y2="18" />
    <line x1="10" y1="18" x2="38" y2="18" opacity="0.6" />
    <line x1="10" y1="18" x2="10" y2="22" />
    <line x1="24" y1="18" x2="24" y2="22" />
    <line x1="38" y1="18" x2="38" y2="22" />
    <rect x="4"  y="22" width="12" height="8" />
    <rect x="18" y="22" width="12" height="8" />
    <rect x="32" y="22" width="12" height="8" />
    <line x1="10" y1="30" x2="10" y2="34" opacity="0.55" />
    <line x1="24" y1="30" x2="24" y2="34" opacity="0.55" />
    <line x1="38" y1="30" x2="38" y2="34" opacity="0.55" />
    <rect x="6"  y="34" width="8"  height="6" opacity="0.55" />
    <rect x="20" y="34" width="8"  height="6" opacity="0.55" />
    <rect x="34" y="34" width="8"  height="6" opacity="0.55" />
  </Frame>
);

const ColumnsBuild = () => (
  <Frame>
    <line x1="10" y1="10" x2="10" y2="40" />
    <line x1="18" y1="16" x2="18" y2="40" />
    <line x1="26" y1="8"  x2="26" y2="40" />
    <line x1="34" y1="14" x2="34" y2="40" />
    <line x1="42" y1="20" x2="42" y2="40" />
    <line x1="6"  y1="40" x2="46" y2="40" opacity="0.6" />
    <line x1="10" y1="22" x2="42" y2="22" opacity="0.3" strokeDasharray="1.5 3" />
  </Frame>
);

const LayeredGrid = () => (
  <Frame>
    <rect x="6"  y="12" width="18" height="14" />
    <rect x="12" y="18" width="18" height="14" />
    <rect x="18" y="24" width="18" height="14" />
    <rect x="24" y="10" width="18" height="14" />
  </Frame>
);

const TempoBars = () => (
  <Frame>
    <line x1="8"  y1="16" x2="42" y2="16" opacity="0.4" strokeDasharray="1.5 3" />
    <line x1="8"  y1="24" x2="42" y2="24" opacity="0.4" strokeDasharray="1.5 3" />
    <line x1="8"  y1="32" x2="42" y2="32" opacity="0.4" strokeDasharray="1.5 3" />
    <circle cx="12" cy="16" r="1.6" fill={STROKE} />
    <circle cx="22" cy="24" r="1.6" fill={STROKE} />
    <circle cx="34" cy="16" r="1.6" fill={STROKE} />
    <circle cx="38" cy="32" r="1.6" fill={STROKE} />
    <circle cx="16" cy="32" r="1.6" fill={STROKE} />
    <line x1="12" y1="16" x2="22" y2="24" opacity="0.7" />
    <line x1="22" y1="24" x2="34" y2="16" opacity="0.7" />
    <line x1="34" y1="16" x2="38" y2="32" opacity="0.7" />
    <line x1="16" y1="32" x2="22" y2="24" opacity="0.7" />
  </Frame>
);

// ---- SIGNATURE MARKERS (used above section headings) ----------------

const ProofSignature = () => (
  <Frame>
    <circle cx="14" cy="34" r="1.6" fill={STROKE} />
    <path d="M 14 34 A 16 16 0 0 1 30 18" />
    <path d="M 14 34 A 24 24 0 0 1 38 10" />
    <circle cx="30" cy="18" r="2" />
    <line x1="14" y1="34" x2="30" y2="18" opacity="0.35" strokeDasharray="1.5 2" />
  </Frame>
);

const ArmSignature = () => (
  <Frame>
    <circle cx="10" cy="10" r="1.6" fill={STROKE} />
    <circle cx="38" cy="10" r="1.6" fill={STROKE} />
    <circle cx="24" cy="38" r="1.6" fill={STROKE} />
    <path d="M 10 10 Q 24 4 38 10" />
    <path d="M 38 10 Q 42 24 24 38" />
    <path d="M 24 38 Q 6 24 10 10" />
    <circle cx="24" cy="20" r="2" />
  </Frame>
);

const BuildSignature = () => (
  <Frame>
    <line x1="6" y1="42" x2="42" y2="42" />
    <rect x="10" y="30" width="8" height="12" />
    <rect x="20" y="20" width="8" height="22" />
    <rect x="30" y="10" width="8" height="32" />
    <line x1="10" y1="26" x2="18" y2="26" opacity="0.4" />
    <line x1="20" y1="16" x2="28" y2="16" opacity="0.4" />
    <line x1="30" y1="20" x2="38" y2="20" opacity="0.4" />
    <line x1="30" y1="30" x2="38" y2="30" opacity="0.4" />
  </Frame>
);

// ---- DIAGNOSTIC / EDITORIAL -----------------------------------------

const AssessmentScope = () => (
  <Frame>
    <circle cx="24" cy="24" r="18" />
    <circle cx="24" cy="24" r="12" opacity="0.6" />
    <line x1="6" y1="24" x2="42" y2="24" opacity="0.5" />
    <line x1="24" y1="6" x2="24" y2="42" opacity="0.5" />
    <circle cx="24" cy="24" r="2" fill={STROKE} />
    <line x1="24" y1="24" x2="36" y2="14" />
    <circle cx="36" cy="14" r="1.6" fill={STROKE} />
  </Frame>
);

const EditorialMark = () => (
  <Frame>
    <line x1="8"  y1="14" x2="40" y2="14" />
    <line x1="8"  y1="22" x2="40" y2="22" opacity="0.6" />
    <line x1="8"  y1="30" x2="34" y2="30" opacity="0.6" />
    <line x1="8"  y1="38" x2="28" y2="38" opacity="0.6" />
    <line x1="6"  y1="10" x2="14" y2="10" opacity="0.4" />
    <line x1="6"  y1="14" x2="6"  y2="42" opacity="0.4" />
  </Frame>
);

// ---- MAIN COMPONENT --------------------------------------------------

const MARKERS = {
  // Signatures
  "proof-signature": ProofSignature,
  "arm-signature": ArmSignature,
  "build-signature": BuildSignature,
  "assessment-scope": AssessmentScope,
  "editorial-mark": EditorialMark,

  // Commercial Proof tiles
  "signal-radar": SignalRadar,
  "map-nodes": MapNodes,
  "crosshair": Crosshair,
  "narrative-arc": NarrativeArc,
  "target-list": TargetList,
  "dialog-dots": DialogDots,
  "fork-arrow": ForkArrow,

  // Commercial Arm tiles
  "pin-location": PinLocation,
  "rhythm-bars": RhythmBars,
  "follow-through": FollowThrough,
  "funnel": Funnel,
  "converging": Converging,
  "loop-flow": LoopFlow,
  "ripple": Ripple,
  "branches": Branches,
  "dot-cluster": DotCluster,
  "integrated-grid": IntegratedGrid,

  // Commercial Build tiles
  "foundation-layers": FoundationLayers,
  "hierarchy-tree": HierarchyTree,
  "columns-build": ColumnsBuild,
  "layered-grid": LayeredGrid,
  "tempo-bars": TempoBars,
};

export default function ServiceMarker({ name }) {
  const Component = MARKERS[name] || ProofSignature;
  return <Component />;
}
